// Reusable internal-link block that surfaces the growth pages (cities,
// industry x service combos), buyer decision guides, and glossary entries.
// Dropped into high-authority pages (Home, Services) to spread link equity
// to the newer SEO landing pages.
import React from "react";
import { Link } from "react-router-dom";
import { Overline } from "@/components/Primitives";

const CITY_LINKS = [
  { to: "/toronto-brand-marketing-agency", label: "Toronto" },
  { to: "/ottawa-brand-marketing-agency", label: "Ottawa" },
  { to: "/ottawa-valley-marketing-agency", label: "Ottawa Valley" },
  { to: "/mississauga-brand-marketing-agency", label: "Mississauga" },
  { to: "/hamilton-brand-marketing-agency", label: "Hamilton" },
  { to: "/kitchener-waterloo-brand-marketing-agency", label: "Kitchener-Waterloo" },
  { to: "/london-ontario-brand-marketing-agency", label: "London ON" },
  { to: "/oakville-brand-marketing-agency", label: "Oakville" },
  { to: "/burlington-brand-marketing-agency", label: "Burlington" },
  { to: "/arnprior-marketing-agency", label: "Arnprior" },
  { to: "/renfrew-marketing-agency", label: "Renfrew" },
  { to: "/pembroke-marketing-agency", label: "Pembroke" },
  { to: "/petawawa-marketing-agency", label: "Petawawa" },
  { to: "/carleton-place-marketing-agency", label: "Carleton Place" },
  { to: "/perth-marketing-agency", label: "Perth" },
  { to: "/almonte-marketing-agency", label: "Almonte" },
];

const COMBO_LINKS = [
  { to: "/website-design-for-contractors-canada", label: "Websites for contractors" },
  { to: "/website-design-for-law-firms-canada", label: "Websites for law firms" },
  { to: "/website-design-for-restaurants-canada", label: "Websites for restaurants" },
  { to: "/branding-for-real-estate-agents-canada", label: "Branding for real estate agents" },
  { to: "/branding-for-dentists-canada", label: "Branding for dentists" },
  { to: "/branding-for-financial-advisors-canada", label: "Branding for financial advisors" },
  { to: "/google-business-profile-for-home-services", label: "GBP for home services" },
  { to: "/google-business-profile-for-restaurants", label: "GBP for restaurants" },
];

const GUIDE_LINKS = [
  { to: "/how-to-choose-a-brand-marketing-agency", label: "How to choose an agency" },
  { to: "/questions-to-ask-before-hiring-a-branding-agency", label: "Questions to ask before hiring" },
  { to: "/signs-you-need-to-rebrand", label: "Signs you need to rebrand" },
  { to: "/signs-your-website-is-costing-you-sales", label: "Signs your website is costing sales" },
  { to: "/kostaja-vs-diy", label: "Kostaja vs DIY" },
  { to: "/kostaja-vs-cheap-logo-mills", label: "Kostaja vs cheap logo mills" },
  { to: "/kostaja-vs-generic-web-designer", label: "Kostaja vs a generic web designer" },
];

const GLOSSARY_LINKS = [
  { to: "/glossary/what-does-a-brand-strategist-do", label: "What does a brand strategist do" },
  { to: "/glossary/what-is-a-brand-system", label: "What is a brand system" },
  { to: "/glossary/what-is-local-seo", label: "What is local SEO" },
  { to: "/glossary/what-is-a-brand-audit", label: "What is a brand audit" },
  { to: "/glossary/what-is-brand-equity", label: "What is brand equity" },
  { to: "/glossary/what-is-a-service-business", label: "What is a service business" },
];

const Chip = ({ to, label, testId }) => (
  <Link
    to={to}
    data-testid={testId}
    className="inline-flex items-center gap-2 px-4 py-2 border border-white/15 text-sm font-semibold hover:bg-[#FFC600] hover:text-black hover:border-[#FFC600] transition-colors"
  >
    <span>{label}</span>
    <span aria-hidden className="text-white/60 group-hover:text-black">→</span>
  </Link>
);

const GrowthLinks = ({ testId = "growth-links" }) => {
  return (
    <section
      data-testid={testId}
      className="bg-black border-b border-white/10"
    >
      <div className="max-w-[1440px] mx-auto px-5 lg:px-10 py-16 lg:py-20 space-y-14">
        <div>
          <Overline className="mb-3">Serving cities across Ontario</Overline>
          <h2 className="text-3xl lg:text-5xl font-black tracking-tight leading-[0.95] max-w-3xl">
            Local pages for the markets Kostaja works in most.
          </h2>
          <p className="mt-4 max-w-2xl text-white/70 text-base">
            Kostaja works remotely with service businesses across Canada. These pages are written for the Ontario cities where most of the work concentrates.
          </p>
          <div className="mt-6 flex flex-wrap gap-3">
            {CITY_LINKS.map((c) => (
              <Chip key={c.to} to={c.to} label={c.label} testId={`growth-city-${c.to.slice(1)}`} />
            ))}
          </div>
        </div>

        <div>
          <Overline className="mb-3">Industry deep-dives</Overline>
          <h2 className="text-3xl lg:text-5xl font-black tracking-tight leading-[0.95] max-w-3xl">
            Websites, branding, and Google Business Profile work by industry.
          </h2>
          <p className="mt-4 max-w-2xl text-white/70 text-base">
            Same Kostaja approach, written for the exact buyer question. Pick the closest fit.
          </p>
          <div className="mt-6 flex flex-wrap gap-3">
            {COMBO_LINKS.map((c) => (
              <Chip key={c.to} to={c.to} label={c.label} testId={`growth-combo-${c.to.slice(1)}`} />
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
          <div>
            <Overline className="mb-3">Buyer decision guides</Overline>
            <h3 className="text-2xl lg:text-3xl font-black tracking-tight leading-[0.95] max-w-md">
              Read these before signing a scope with any agency.
            </h3>
            <ul className="mt-6 divide-y divide-white/10 border-y border-white/15">
              {GUIDE_LINKS.map((g) => (
                <li key={g.to}>
                  <Link
                    to={g.to}
                    data-testid={`growth-guide-${g.to.slice(1)}`}
                    className="flex items-center justify-between py-4 group"
                  >
                    <span className="text-base font-semibold group-hover:text-[#FFC600] transition-colors">{g.label}</span>
                    <span className="text-white/40 group-hover:text-[#FFC600] transition-colors">→</span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <Overline className="mb-3">Glossary</Overline>
            <h3 className="text-2xl lg:text-3xl font-black tracking-tight leading-[0.95] max-w-md">
              Plain-English definitions for the terms buyers actually ask about.
            </h3>
            <ul className="mt-6 divide-y divide-white/10 border-y border-white/15">
              {GLOSSARY_LINKS.map((g) => (
                <li key={g.to}>
                  <Link
                    to={g.to}
                    data-testid={`growth-glossary-${g.to.replace(/\//g, "-").slice(1)}`}
                    className="flex items-center justify-between py-4 group"
                  >
                    <span className="text-base font-semibold group-hover:text-[#FFC600] transition-colors">{g.label}</span>
                    <span className="text-white/40 group-hover:text-[#FFC600] transition-colors">→</span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
};

export default GrowthLinks;
